import React, { useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useForm, Controller } from "react-hook-form";
import { useTermsViewModel } from "@/features/terms/viewmodel/useTermsViewModel";
import { goalsService } from "../service";
import {
  CreateGoalRequestType,
  FundingGoal,
  FundingGoalEntityType,
} from "../model/types";
import { fetchGoalsRequested } from "../store";
import { useUnit } from "effector-react";
import { useNavigate } from "react-router-dom";
import { showToast } from "@/core/store/app";

export interface CreateGoalModalProps {
  visible: boolean;
  onHide: () => void;
  goal?: FundingGoal | null;
  entity?: FundingGoalEntityType | null;
}

type FormData = {
  title: string;
  field_funding_expenditure: string[];
  field_funding_range: string[];
  field_funding_goal_funding_type: string[];
  field_sectors: string[];
};

export const CreateGoalModal: React.FC<CreateGoalModalProps> = ({
  visible,
  onHide,
  goal,
  entity,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [sectorQuery, setSectorQuery] = useState("");
  const store = useTermsViewModel();
  const isEditMode = !!goal;
  const fetchGoals = useUnit(fetchGoalsRequested);
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      title: "",
      field_funding_expenditure: [],
      field_funding_range: [],
      field_funding_goal_funding_type: [],
      field_sectors: [],
    },
  });

  const selectedCategories = new Set(watch("field_funding_expenditure"));
  const selectedBudgets = new Set(watch("field_funding_range"));
  const selectedTypes = new Set(watch("field_funding_goal_funding_type"));
  const selectedSectors = new Set(watch("field_sectors"));

  const field_funding_goal_funding_type = store.fundingTypes.filter((item) =>
    entity?.field_funding_goal_funding_type.includes(item.tid),
  );

  const field_sectors = store.sectors.filter((item) =>
    entity?.field_sectors.includes(item.tid),
  );

  const field_funding_expenditure = store.expenditures.filter(
    (item) => item.tid === entity?.field_funding_expenditure[0],
  );

  const field_funding_range = store.fundingRanges.filter(
    (item) => item.tid === entity?.field_funding_range[0],
  )[0];

  useEffect(() => {
    if (visible) {
      if (isEditMode && goal && entity) {
        reset({
          title: goal.title,
          field_funding_expenditure: field_funding_expenditure[0]
            ? [field_funding_expenditure[0].tid]
            : [],
          field_funding_range: field_funding_range
            ? [field_funding_range.tid]
            : [],
          field_funding_goal_funding_type: field_funding_goal_funding_type.map(
            (item) => item.tid,
          ),
          field_sectors: field_sectors?.map((s) => s.tid) || [],
        });
      } else if (!isEditMode) {
        reset();
      }
      setStep(1);
    }
  }, [visible, isEditMode, goal, entity, reset]);

  const onSubmit = async (data: FormData) => {
    const payload: CreateGoalRequestType = {
      title: data.title,
      field_funding_expenditure: data.field_funding_expenditure,
      field_funding_range: data.field_funding_range,
      field_funding_goal_funding_type: data.field_funding_goal_funding_type,
      field_sectors: data.field_sectors,
    };

    try {
      if (isEditMode && goal) {
        const response = await goalsService.updateGoal(goal.id, payload);
        // console.log("Update response:", response);

        // ✅ Navigate immediately after successful update
        if (response.uid) {
          onHide();
          navigate(`/goals/${response.uid}/matches`);
        }
      } else {
        // if (payload.title.length === 0) {
        //   showToast({
        //     type: "error",
        //     text: "Please fill in all required fields",
        //   });
        //   return;
        // }

        const response = await goalsService.createGoal(payload);
        // console.log("Create response:", response);

        // ✅ Navigate immediately after successful creation
        if (response.uid) {
          fetchGoals(undefined);
          onHide();
          navigate(`/goals/${response.uid}/matches`);
        } else {
          console.error("No UID returned from create goal");
        }
      }
    } catch (err: any) {
      console.error("Failed to save goal", err);
      // err.errors.map((item: string) => {
      //   return showToast({
      //     type: "error",
      //     text: item,
      //   });
      // });
      showToast({
        type: "error",
        text: "Please fill in all required fields",
      });
    }
  };

  type SectorNode = { id: string; label: string; children?: SectorNode[] };
  const sectorsTree: SectorNode[] = useMemo(() => {
    if (!store.sectors || store.sectors.length === 0) {
      return [];
    }

    const nodes: Record<string, SectorNode> = {};
    const tree: SectorNode[] = [];

    store.sectors.forEach((sector) => {
      nodes[sector.tid] = {
        id: sector.tid,
        label: sector.name,
        children: [],
      };
    });

    store.sectors.forEach((sector) => {
      if (sector.parent && nodes[sector.parent]) {
        nodes[sector.parent].children?.push(nodes[sector.tid]);
      } else {
        tree.push(nodes[sector.tid]);
      }
    });

    return tree;
  }, [store.sectors]);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const handleToggleType = (id: string) => {
    const currentTypes = watch("field_funding_goal_funding_type");
    const newTypes = currentTypes.includes(id)
      ? currentTypes.filter((t) => t !== id)
      : [...currentTypes, id];
    setValue("field_funding_goal_funding_type", newTypes);
  };

  const handleToggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSector = (id: string) => {
    const findNode = (
      nodes: SectorNode[],
      nodeId: string,
    ): SectorNode | null => {
      for (const node of nodes) {
        if (node.id === nodeId) return node;
        if (node.children) {
          const found = findNode(node.children, nodeId);
          if (found) return found;
        }
      }
      return null;
    };

    const getDescendants = (node: SectorNode): string[] => {
      let ids: string[] = [];
      if (node.children) {
        for (const child of node.children) {
          ids.push(child.id);
          ids = ids.concat(getDescendants(child));
        }
      }
      return ids;
    };

    const currentSectors = new Set(watch("field_sectors"));
    const node = findNode(sectorsTree, id);
    const descendants = node ? getDescendants(node) : [];
    const allIds = [id, ...descendants];

    if (currentSectors.has(id)) {
      allIds.forEach((i) => currentSectors.delete(i));
    } else {
      allIds.forEach((i) => currentSectors.add(i));
    }
    setValue("field_sectors", Array.from(currentSectors));
  };

  const renderTree = (nodes: SectorNode[], depth = 0) => (
    <ul className="sector-tree" style={{ marginLeft: depth ? 30 : 0 }}>
      {nodes
        .filter(
          (n) =>
            !sectorQuery ||
            n.label.toLowerCase().includes(sectorQuery.toLowerCase()) ||
            (n.children &&
              n.children.some((c) =>
                c.label.toLowerCase().includes(sectorQuery.toLowerCase()),
              )),
        )
        .map((n) => (
          <li key={n.id}>
            <div className="tree-row">
              {n.children && n.children.length > 0 ? (
                <button
                  type="button"
                  className={`tree-toggle ${expanded.has(n.id) ? "open" : ""}`}
                  onClick={() => handleToggleExpand(n.id)}
                >
                  <i
                    className={`pi ${
                      expanded.has(n.id)
                        ? "pi-chevron-down"
                        : "pi-chevron-right"
                    }`}
                  />
                </button>
              ) : (
                <span className="tree-spacer" />
              )}
              <input
                type="checkbox"
                checked={selectedSectors.has(n.id)}
                onChange={() => toggleSector(n.id)}
              />
              <span className="tree-label">{n.label}</span>
            </div>
            {n.children &&
              expanded.has(n.id) &&
              renderTree(n.children, depth + 1)}
          </li>
        ))}
    </ul>
  );

  const footer =
    step === 1 ? (
      <div className="create-goal__footer">
        <Button label="Cancel" text onClick={onHide} />
        <Button label="Next" onClick={() => setStep(2)} />
      </div>
    ) : (
      <div className="create-goal__footer">
        <Button label="Previous" text onClick={() => setStep(1)} />
        <Button
          label={isEditMode ? "Update goal" : "Create goal"}
          onClick={handleSubmit(onSubmit)}
          type="submit"
        />
      </div>
    );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={isEditMode ? "Edit funding goal" : "Create a new funding goal"}
      className="create-goal"
      breakpoints={{ "960px": "75vw", "640px": "90vw" }}
      style={{ width: "720px" }}
      footer={footer}
      draggable={false}
    >
      <form>
        {step === 1 && (
          <>
            <div className="create-goal__section">
              <div className="section-title">Select funding category</div>
              <div className="pill-grid">
                {store.expenditures.map((c) => (
                  <button
                    type="button"
                    key={c.tid}
                    className={`pill ${
                      selectedCategories.has(c.tid) ? "active" : ""
                    }`}
                    onClick={() =>
                      setValue("field_funding_expenditure", [c.tid])
                    }
                  >
                    <span className="dot" /> {c.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="create-goal__section">
              <div className="section-title">Name of funding goal</div>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    className="text-input"
                    placeholder="My equipment goal"
                  />
                )}
              />
            </div>
            <div className="create-goal__section">
              <div className="section-title">Select budget range</div>
              <div className="pill-grid">
                {store.fundingRanges.map((b) => (
                  <button
                    type="button"
                    key={b.tid}
                    className={`pill ${
                      selectedBudgets.has(b.tid) ? "active" : ""
                    }`}
                    onClick={() => setValue("field_funding_range", [b.tid])}
                  >
                    <span className="dot" /> {b.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="create-goal__section">
              <div className="section-title">Select type of funding</div>
              <div className="pill-grid">
                {store.fundingTypes.map((t) => (
                  <button
                    type="button"
                    key={t.tid}
                    className={`pill ${
                      selectedTypes.has(t.tid) ? "checked" : ""
                    }`}
                    onClick={() => handleToggleType(t.tid)}
                  >
                    <span className="checkbox" /> {t.name}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="create-goal__section">
              <div className="section-title">
                Select sectors related to the activities you're aiming to fund
              </div>
              <div className="selected-chips">
                {Array.from(selectedSectors).map((id) => {
                  const findLabel = (nodes: SectorNode[]): string | null => {
                    for (const n of nodes) {
                      if (n.id === id) return n.label;
                      if (n.children) {
                        const match = findLabel(n.children);
                        if (match) return match;
                      }
                    }
                    return null;
                  };
                  const label = findLabel(sectorsTree) || id;
                  return (
                    <span
                      key={id}
                      className="chip-tag"
                      onClick={() => toggleSector(id)}
                    >
                      {label} <span className="close">×</span>
                    </span>
                  );
                })}
              </div>
              <input
                className="text-input search-input"
                placeholder="Search Sectors"
                value={sectorQuery}
                onChange={(e) => setSectorQuery(e.target.value)}
              />
            </div>

            <div className="create-goal__section">
              <div className="sector-tree-wrap">{renderTree(sectorsTree)}</div>
            </div>

            <div className="step-info">Step 2 of 2</div>
          </>
        )}
      </form>
    </Dialog>
  );
};
